import './rejectRequestPopup.scss';

import { Popup } from '@approvalmax/ui';
import { mentionHelpers } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { selectors, ui } from 'modules/common';
import { domain, State } from 'modules/data';
import { useShallowEqualSelector } from 'modules/react-redux';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { useCompanyMentionItems } from 'shared/hooks/useCompanyMentionItems';

import { makeDecision } from '../actions';
import { addAsWatcherPopupState } from '../components/AddAsWatchersPopup/AddAsWatchersPopup.states';
import { useSelectedFilter } from '../hooks';
import { RejectRequestPopupData } from '../reducers/page/activePopup/rejectRequestPopupReducer';
import { getActivePopup, isActiveRequestLocked } from '../selectors/pageSelectors';
import { getMentionedUsersWithoutAccessToRequest } from '../utils/getUsersWithoutAccessToRequest';
import { messages } from './RejectRequestPopup.messages';

const bem = bemFactory.block('reql-reject-request-popup');
const qa = bemFactory.qa('reql-reject-request-popup');

export const RejectRequestPopup = memo(() => {
    const dispatch = useDispatch();
    const selectedFilter = useSelectedFilter();
    const setWatchersPopup = useSetAtom(addAsWatcherPopupState);

    const [commentText, setCommentText] = useState('');

    const { request, popupData, isLocked } = useShallowEqualSelector((state: State) => {
        const { requestId } = getActivePopup<RejectRequestPopupData>(state);
        const request = selectors.request.getRequestById(state, requestId);

        return {
            request,
            popupData: getActivePopup<RejectRequestPopupData>(state),
            isLocked: isActiveRequestLocked(state),
        };
    });
    const { handleGetMentionItems } = useCompanyMentionItems(request.company);
    const { mentionedUserIds } = useMemo(() => mentionHelpers.prepareCommentPayload(commentText), [commentText]);

    const handleReject = useCallback(() => {
        dispatch(
            makeDecision({
                target: request,
                decision: domain.RequestStepParticipantDecision.Reject,
                selectedFilter,
                commentText: commentText || null,
                showResultsExplicitly: popupData.showResultsExplicitly,
                mentionedUserIds,
            })
        );
    }, [commentText, dispatch, mentionedUserIds, popupData.showResultsExplicitly, request, selectedFilter]);

    const handleSubmit = useCallback(() => {
        const usersWithoutAccess = getMentionedUsersWithoutAccessToRequest(request, mentionedUserIds);

        if (usersWithoutAccess.length) {
            setWatchersPopup((state) => ({ ...state, usersWithoutAccess }));
        } else {
            handleReject();
        }
    }, [request, mentionedUserIds, setWatchersPopup, handleReject]);

    useEffect(() => {
        setWatchersPopup((state) => ({ ...state, onSubmit: handleReject }));
    }, [handleReject, setWatchersPopup]);

    return (
        <Popup.DefaultContent
            className={bem()}
            qa={qa()}
            title={messages.popupTitle({ requestName: request.displayName })}
            buttons={
                <Popup.DefaultContent.Button
                    qa={qa('done-button')}
                    disabled={isLocked}
                    execute={handleSubmit}
                    title={messages.rejectButtonTitle}
                >
                    {messages.rejectButtonText}
                </Popup.DefaultContent.Button>
            }
        >
            <div className={bem('content')}>
                <div className={bem('description')}>
                    {messages.descriptionText({ authorName: request.author.displayName })}
                </div>

                <ui.AddCommentEditor
                    focusOnMount
                    className={bem('comment-editor')}
                    qa={qa('comment-editor')}
                    value={commentText}
                    onChange={setCommentText}
                    allowMention
                    mentionItems={handleGetMentionItems}
                />
            </div>
        </Popup.DefaultContent>
    );
});
